import { createFileRoute } from "@tanstack/react-router";
import { sameCompanyFaq } from "@/lib/brand";
import { HOURLY_MINIMUM_HOURS, WEEKEND_MINIMUM_HOURS, hourlyMinimumFaq } from "@/lib/booking-minimums";
import { SAFETY_FACTS } from "@/lib/safety-facts";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-limo-hotel-arrival-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-yellow-cabin.jpg?format=webp&quality=72&w=1000&imagetools";
import introImage from "@/assets/sprinter-lounge.jpg?format=webp&quality=72&w=1200&imagetools";
import suvImage from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1200&imagetools";
import galleryLimo from "@/assets/sprinter-limo-2.jpg?format=webp&quality=72&w=900&imagetools";
import galleryExec from "@/assets/sprinter-executive-2.jpg?format=webp&quality=72&w=900&imagetools";
import galleryJet from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=900&imagetools";
import gallerySuv from "@/assets/suv-suburban-exterior.webp?format=webp&quality=72&w=900&imagetools";
import faqImage from "@/assets/sprinter-exec-blue.jpg?format=webp&quality=72&w=900&imagetools";
import airportCard from "@/assets/sprinter-exec-red.jpg?format=webp&quality=70&w=800&imagetools";
import suburbanCard from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=70&w=800&imagetools";
import limoCard from "@/assets/sprinter-limo-party.jpg?format=webp&quality=70&w=800&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/thanksgiving-transportation-atlanta";
const PAGE_TITLE = "Thanksgiving Airport & Family Transportation Atlanta | Sprinter";
const PAGE_DESC =
  "Thanksgiving in Atlanta, handled: ATL airport pickups and the whole family together in a chauffeured Mercedes-Benz Sprinter, up to 14 guests. From $250/hour. Get a quote.";

const AIRPORT = "Airport & Travel";
const PRICING = "Pricing & Booking";
const FAMILY = "Your Family's Holiday";

const faqs = [
  {
    group: AIRPORT,
    q: "Can you pick up our family at Hartsfield-Jackson for Thanksgiving?",
    a: "Yes. We track your flight on every airport transfer, so your chauffeur plans around your real arrival time. Airport transfers are quoted as a flat door-to-door rate, and a Sprinter fits the family and the holiday luggage in one trip.",
  },
  {
    group: AIRPORT,
    q: "Will all our luggage fit?",
    a: "Text me how many guests and bags you have. We match the vehicle to the group and the luggage, and suggest a second vehicle if a big family needs the room.",
  },
  {
    group: AIRPORT,
    q: "Can you help when we travel outside Atlanta?",
    a: "Yes. Every vehicle in Atlanta is ours. When your family travels beyond Atlanta, we arrange a trusted partner where you land, and you keep one point of contact with us for every ride.",
  },
  {
    group: PRICING,
    q: "How much does Thanksgiving transportation cost?",
    a: "Sprinters start from $250 per hour, and airport transfers are quoted as a flat rate. Every booking gets a custom quote in writing before you book. A 50% deposit secures your date.",
  },
  hourlyMinimumFaq({ transfers: true, weekend: true, group: PRICING }),
  {
    group: PRICING,
    q: "When should we book for Thanksgiving week?",
    a: "As early as you can. The days before Thanksgiving, Thanksgiving Day and the Sunday after are some of the busiest travel days of the year, and our fleet is our own, not an endless pool of cars.",
  },
  sameCompanyFaq({ group: PRICING }),
  {
    group: FAMILY,
    q: "Can one Sprinter take the whole family to Thanksgiving dinner?",
    a: "Yes. Our Sprinter Limousine seats up to 14, so everyone arrives together and nobody has to be the designated driver. Book by the hour and your chauffeur waits while you eat.",
  },
  {
    group: FAMILY,
    q: "Is alcohol allowed?",
    a: "BYOB for guests 21 and over. Glassware and ice are provided; we do not sell or provide alcohol.",
  },
  {
    group: FAMILY,
    q: "Are your chauffeurs background-checked?",
    a: "Yes. We are registered with the State of Georgia and commercially insured. Every chauffeur is fingerprinted and background-checked, and our vehicles have cameras and AI driver-safety technology.",
  },
];

export const Route = createFileRoute("/thanksgiving-transportation-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "thanksgiving airport transportation atlanta, thanksgiving car service atlanta, thanksgiving limo atlanta, family airport pickup atlanta, sprinter rental thanksgiving atlanta, holiday transportation atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Thanksgiving Transportation Atlanta",
            image: "https://atlantaluxurysprinters.com/favicon.png",
            description: PAGE_DESC,
            serviceType: "Holiday Airport and Family Transportation",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Marietta" },
              { type: "City", name: "Alpharetta" },
              { type: "City", name: "Decatur" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Thanksgiving • Metro Atlanta"
      h1="Thanksgiving Travel, Handled. The Whole Family, One Sprinter."
      intro="You're coordinating flights, family and luggage. Let me help with ATL arrivals, dinner trips and longer drives in our Mercedes-Benz Sprinter Limousines and Suburban SUVs."
      heroImage={heroImage}
      heroAlt="Black Mercedes-Benz Sprinter Limousine at a hotel entrance in Atlanta"
      rich
      quickFacts={[
        { value: "Up to 14", label: "The whole family rides together" },
        { value: "From $250/hr", label: "Airport transfers at a flat rate" },
        { value: "Flight tracked", label: "On every airport pickup" },
        { value: "One contact", label: "Every ride, even beyond Atlanta" },
      ]}
      introImage={introImage}
      introImageAlt="Sprinter Limousine lounge with wraparound leather seating"
      bookingBands={[
        { title: "Check Your Thanksgiving Dates", body: "Send us the flights, the addresses and the group size. We reply with a written quote." },
        { title: "Reserve Your Sprinter for Thanksgiving Week", body: "Sprinters start from $250/hour. A 50% deposit secures your date." },
      ]}
      faqImage={faqImage}
      faqImageAlt="Executive Sprinter cabin with blue LED lighting"
      midImage={midImage}
      midAlt="Executive Sprinter cabin with individual leather seats"
      sectionOneTitle="Holiday Travel Without the Stress"
      sectionOneBody={[
        "Thanksgiving week means packed terminals and someone in the family stuck driving back and forth to the airport.",
        "We take that off your plate. Your chauffeur meets the family at Hartsfield-Jackson, the luggage goes in the back, and everyone rides together in one Sprinter to the house, the hotel or straight to dinner.",
        "We are the luxury Sprinter fleet of Empress Elite Limousine, a family-run company, and we own every vehicle in our Atlanta fleet. You deal with us directly, from the first quote to the last drop-off.",
      ]}
      bulletsTitle="What Your Thanksgiving Booking Includes"
      bulletsIntro="Book an airport transfer, a few hours for dinner, or the whole week. Every ride is planned around your family's schedule."
      bullets={[
        "Sprinter Limousine seating for up to 14, so the family rides together",
        "We track your flight on every airport transfer",
        "Room for holiday luggage, or a second vehicle for big families",
        "One chauffeur who stays with you for hourly bookings",
        "Built-in cooler; BYOB for guests 21+ with glassware and ice provided",
        "Track your chauffeur live in our app, so you always know when they'll pull up",
        "Your quote in writing before you book",
      ]}
      coverageTitle="Thanksgiving Pickups Across Metro Atlanta"
      coverageIntro="Home, hotel and airport pickups anywhere in metro Atlanta. Farther out? Ask and we will quote it."
      coverage={[
        "Hartsfield-Jackson (ATL)",
        "Buckhead & Midtown",
        "Sandy Springs & Dunwoody",
        "Alpharetta & Roswell",
        "Marietta & Kennesaw",
        "Decatur & DeKalb",
        "Peachtree City & Fayetteville",
        "Lawrenceville & Gwinnett",
        "PDK, Fulton County & Cobb County airports",
      ]}
      detailSections={[
        {
          title: "Airport Arrivals at Hartsfield-Jackson",
          body: [
            "Family flying in from out of town? We track every flight, so a delay doesn't mean a scramble. Your chauffeur plans around the real arrival time and helps with the bags.",
          ],
          list: [
            "Flat door-to-door rate for airport transfers.",
            "A Sprinter fits a big family and the luggage in one trip.",
            "Track your chauffeur live in our app, so you always know when they'll pull up.",
            "Return trips to the airport on Sunday, booked at the same time.",
          ],
          image: suvImage,
          imageAlt: "Suburban SUV with captain seats for family airport pickups",
        },
        {
          title: "Get the Whole Family There Together",
          body: [
            "Book by the hour and one Sprinter stays with your family for the day. Nobody has to drive, and everybody arrives at the same time.",
          ],
          list: [
            "Thanksgiving dinner at a relative's home or a restaurant, with your chauffeur waiting.",
            "A day out with the out-of-town relatives around Atlanta.",
            "Black Friday shopping with room for the bags.",
            "The Falcons game or a holiday event downtown.",
            `Hourly service has a ${HOURLY_MINIMUM_HOURS}-hour minimum, and ${WEEKEND_MINIMUM_HOURS} hours on weekends.`,
          ],
        },
        {
          title: "Traveling Beyond Atlanta? One Point of Contact",
          body: [
            "Every vehicle in Atlanta is ours. When your family travels beyond Atlanta, we arrange a trusted partner where you land, so you keep one point of contact with us for every ride.",
            "One company handles the transportation, so you don't have to worry about it. Text me where you're going.",
          ],
        },
        {
          title: "Thanksgiving Dates to Book Early",
          body: [
            "These are the busiest travel days of the holiday. If your plans touch any of them, text me early:",
          ],
          list: [
            "Tuesday and Wednesday before Thanksgiving: airport arrivals.",
            "Thanksgiving Day: rides to and from dinner.",
            "The Sunday after Thanksgiving: airport departures.",
          ],
        },
        {
          title: "Safe, Licensed and Insured",
          list: SAFETY_FACTS,
        },
        {
          title: "Which Vehicle Fits Your Family?",
          list: [
            "Up to 14 guests: Sprinter Limousine (Limo Style).",
            "Up to 13 who prefer individual seats: Executive Sprinter.",
            "Up to 7 in first-class comfort: Jet Sprinter.",
            "Smaller family: Suburban SUV, up to 6 with captain seats or 7 with a bench.",
          ],
        },
      ]}
      gallery={{
        title: "Our Fleet for Thanksgiving Week",
        intro: "Real photos of the vehicles we own and operate in Atlanta.",
        images: [
          { src: galleryLimo, alt: "Sprinter Limousine wraparound seating", caption: "Sprinter Limousine, up to 14" },
          { src: galleryExec, alt: "Executive Sprinter individual leather seats", caption: "Executive Sprinter, up to 13" },
          { src: galleryJet, alt: "Jet Style Sprinter white leather captain seats", caption: "Jet Sprinter, up to 7" },
          { src: gallerySuv, alt: "Black Suburban SUV exterior", caption: "Suburban SUV, up to 6 or 7" },
        ],
      }}
      faqs={faqs}
      faqTitle="Thanksgiving Transportation FAQs"
      relatedTitle="Related Services"
      related={[
        { to: "/atlanta-airport-car-service", title: "Atlanta Airport Car Service", desc: "Flat-rate transfers to and from every Atlanta airport.", image: airportCard },
        { to: "/suburban-chauffeur-service-atlanta", title: "Suburban SUV Service", desc: "For smaller families and airport luggage.", image: suburbanCard },
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "Up to 14 guests in our Sprinter Limousine.", image: limoCard },
      ]}
      ctaTitle="Text Me Your Thanksgiving Plans"
      ctaBody="Send me your dates, flights, pickup addresses and group size. I'll check availability myself and send your quote in writing."
    />
  );
}
